import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Timestamp, DocumentData } from "firebase/firestore";
import { User } from "firebase/auth";
import PlusCircleIcon from "@heroicons/react/24/outline/PlusCircleIcon";
import DocumentIcon from "@heroicons/react/24/outline/DocumentIcon";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";
import MinusIcon from "@heroicons/react/24/outline/MinusIcon";
import CheckCircleSolidIcon from "@heroicons/react/24/solid/CheckCircleIcon";

// Models
import {
  ExistingMembership,
  getReadableMembershipStatus,
  InvoiceOverrideData,
  MembershipManager,
} from "../../models/membership";
import {
  ExistingMembershipTemplate,
  InvoiceFrequency,
  getReadableInvoiceMethod,
} from "../../models/membership-template";
import { ExistingCustomer } from "../../models/customer";
import { ExistingCustomerLocation } from "../../models/customer-location";
import { ExistingStiltInvoice, StiltInvoiceStatus } from "../../models/invoice";
import { ExistingTask } from "../../models/task";
import { TaskStatus, getReadableTaskStatus } from "../../models/task-status";
import { SiteKeyUserPermissions } from "../../models/site-key-user-permissions";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";

// Components
import { CustomerDetails } from "../estimates/CustomerDetails";
import NavButtonBack from "../NavButtonBack";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import AlertV2 from "../AlertV2";
import MembershipTasksOverview from "../Memberships/MembershipTasksOverview";
import MembershipTimelineHorizontal from "../Memberships/MembershipTimelineHorizontal";
import LoadingClipboardAnimation from "../LoadingClipBoardAnimation";
import StiltLink from "../StiltLink";

// Utilities
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import currencyFormatter from "../../assets/js/currencyFormatter";
import { DbRead, DbWrite } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import { logger } from "../../logging";
import { WORK_RECORD_AND_TASKS_URL, MEMBERSHIP_TEMPLATES } from "../../urls";
import { useAuthStore } from "../../store/firebase-auth";
import { useUserPermissionsStore } from "../../store/user-permissions";
import * as strings from "../../strings";

interface Payment {
  id: string;
  amount: number;
  date: Timestamp;
  method: string;
  status: "completed" | "pending" | "refunded";
}

interface Props {
  siteKey: string;
}

export default function ViewMembershipContainer(props: Props) {
  const { membershipID } = useParams<{ membershipID: string }>();
  const navigate = useNavigate();
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );

  // State for data
  const [membership, setMembership] = useState<ExistingMembership | null>(null);
  const [membershipTemplate, setMembershipTemplate] =
    useState<ExistingMembershipTemplate | null>(null);
  const [customer, setCustomer] = useState<ExistingCustomer | null>(null);
  const [customerLocation, setCustomerLocation] =
    useState<ExistingCustomerLocation | null>(null);
  const [membershipInvoices, setMembershipInvoices] = useState<
    ExistingStiltInvoice[]
  >([]);
  const [customerInvoices, setCustomerInvoices] = useState<
    ExistingStiltInvoice[]
  >([]);
  const [tasks, setTasks] = useState<ExistingTask[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);

  // Added state for location options
  const [customerLocations, setCustomerLocations] = useState<
    ExistingCustomerLocation[]
  >([]);
  const [businessLocations, setBusinessLocations] = useState<
    ExistingSiteKeyLocation[]
  >([]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isLoadingCustomerInvoices, setIsLoadingCustomerInvoices] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noInvoicesFound, setNoInvoicesFound] = useState(false);

  // State for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editedMembership, setEditedMembership] = useState<
    Partial<ExistingMembership>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "payments">("details");

  // Update the tasks state to include all tasks, not just membership tasks
  const [allTasks, setAllTasks] = useState<ExistingTask[]>([]);
  const [membershipTasks, setMembershipTasks] = useState<ExistingTask[]>([]);
  const [otherTasks, setOtherTasks] = useState<ExistingTask[]>([]);
  const [customerIDForTasks, setCustomerIDForTasks] = useState<string | null>(
    null,
  );

  // Add state for Stilton AI membership overview
  const [isLoadingStiltonOverview, setIsLoadingStiltonOverview] =
    useState(false);
  const [stiltonOverview, setStiltonOverview] = useState<string | null>(null);
  const [showStiltonOverview, setShowStiltonOverview] = useState(true);

  // Update the customer ID for tasks when membership changes
  useEffect(() => {
    if (membership && membership.customerID) {
      setCustomerIDForTasks(membership.customerID);
    }
  }, [membership]);

  // Fetch membership data
  useEffect(() => {
    if (!membershipID) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.memberships.subscribe({
        siteKey: props.siteKey,
        membershipDocID: membershipID,
        onChange: (membershipDoc) => {
          setMembership(membershipDoc);
          setIsLoading(false);
        },
        onError: (error) => {
          logger.error("Error fetching membership", error);
          setError("Failed to load membership data");
          setIsLoading(false);
        },
      });
    } catch (error) {
      logger.error("Error in membership subscription", error);
      setError("Failed to load membership data");
      setIsLoading(false);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [membershipID, props.siteKey]);

  // Fetch invoices for membership
  useEffect(() => {
    if (!membershipID) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.invoices.subscribeByMembershipID({
        siteKey: props.siteKey,
        membershipID: membershipID,
        onChange: (invoices) => {
          if (Array.isArray(invoices)) {
            setMembershipInvoices(invoices);
            setNoInvoicesFound(invoices.length === 0);
          } else if (invoices) {
            // Handle case where API returns a single invoice
            setMembershipInvoices([invoices]);
            setNoInvoicesFound(false);
          } else {
            setMembershipInvoices([]);
            setNoInvoicesFound(true);
          }
        },
        onError: (error) => {
          logger.error("Error fetching invoices for membership", error);
          setNoInvoicesFound(true);
        },
      });
    } catch (error) {
      logger.error("Error setting up invoice subscription", error);
      setNoInvoicesFound(true);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [membershipID, props.siteKey]);

  // Fetch all customer invoices when no membership invoices are found
  useEffect(() => {
    if (!noInvoicesFound || !customer?.id) return;

    const fetchCustomerInvoices = async () => {
      setIsLoadingCustomerInvoices(true);
      try {
        // Fix property name to match actual API
        const invoices = await DbRead.invoices.getByCustomerId(
          props.siteKey,
          customer.id,
        );
        // Remove canceled invoices
        const activeInvoices = invoices?.filter(
          (invoice) => invoice.status !== StiltInvoiceStatus.CANCELED,
        );
        setCustomerInvoices(activeInvoices || []);
      } catch (error) {
        logger.error("Error fetching customer invoices", error);
      } finally {
        setIsLoadingCustomerInvoices(false);
      }
    };

    fetchCustomerInvoices();
  }, [noInvoicesFound, customer?.id, props.siteKey]);

  // Fetch membership template
  useEffect(() => {
    if (!membership?.membershipTemplateID) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.membershipTemplates.subscribe({
        siteKey: props.siteKey,
        templateID: membership.membershipTemplateID,
        onChange: (template) => {
          setMembershipTemplate(template);
        },
        onError: (error) => {
          logger.error("Error fetching membership template", error);
        },
      });
    } catch (error) {
      logger.error("Error setting up template subscription", error);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [membership?.membershipTemplateID, props.siteKey]);

  // Fetch customer data
  useEffect(() => {
    if (!membership?.customerID) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.customers.subscribe({
        customerID: membership.customerID,
        siteKey: props.siteKey,
        onChange: (customerDoc) => {
          setCustomer(customerDoc);
        },
        onError: (error) => {
          logger.error("Error fetching customer", error);
        },
      });
    } catch (error) {
      logger.error("Error setting up customer subscription", error);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [membership?.customerID, props.siteKey]);

  // Fetch customer location
  useEffect(() => {
    if (!membership?.customerLocationID) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.customerLocations.subscribe({
        customerLocationID: membership.customerLocationID,
        siteKey: props.siteKey,
        onChange: (location) => {
          setCustomerLocation(location);
        },
        onError: (error) => {
          logger.error("Error fetching customer location", error);
        },
      });
    } catch (error) {
      logger.error("Error setting up customer location subscription", error);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [membership?.customerLocationID, props.siteKey]);

  // Fetch all customer locations for the dropdown when editing
  useEffect(() => {
    if (!customer?.id || !isEditing) return;

    let unsubscribeFn: (() => void) | undefined;

    try {
      unsubscribeFn = DbRead.customerLocations.subscribeByCustomerID({
        customerID: customer.id,
        siteKey: props.siteKey,
        onChange: (locations) => {
          setCustomerLocations(locations || []);
        },
        onError: (error) => {
          logger.error("Error fetching customer locations", error);
        },
      });
    } catch (error) {
      logger.error("Error setting up customer locations subscription", error);
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [customer?.id, isEditing, props.siteKey]);

  // Fetch business locations
  useEffect(() => {
    if (!isEditing) return;

    const fetchBusinessLocations = async () => {
      try {
        const locations = await DbRead.siteKeyLocations.getAll(props.siteKey);
        setBusinessLocations(locations || []);
      } catch (error) {
        logger.error("Error fetching business locations", error);
      }
    };

    fetchBusinessLocations();
  }, [isEditing, props.siteKey]);

  // Fetch tasks - modify the existing useEffect to fetch all tasks
  useEffect(() => {
    if (!membershipID || !userPermissions || !customerIDForTasks) return;

    async function getTasks() {
      setIsLoadingTasks(true);
      try {
        // Get all tasks for this customer
        let customerTasks = await DbRead.tasks.getSingleCustomerTasks({
          permissions: userPermissions as SiteKeyUserPermissions,
          customerID: customerIDForTasks as string,
          siteKey: props.siteKey,
        });

        if (membership?.customerLocationID) {
          customerTasks = customerTasks.filter(
            (task) => task.customerLocationID === membership.customerLocationID,
          );
        }

        // Store all tasks
        setAllTasks(customerTasks);

        // Filter tasks for this membership
        const membershipRelatedTasks = customerTasks.filter(
          (task) =>
            task.membershipIDs &&
            task.membershipIDs.includes(membershipID as string),
        );

        // Filter tasks not in this membership but for this customer
        const otherCustomerTasks = customerTasks.filter(
          (task) =>
            !task.membershipIDs ||
            !task.membershipIDs.includes(membershipID as string),
        );

        setMembershipTasks(membershipRelatedTasks);
        setOtherTasks(otherCustomerTasks);
        setIsLoadingTasks(false);
      } catch (error) {
        logger.error("Error getting tasks", error);
        setIsLoadingTasks(false);
      }
    }

    getTasks();
  }, [membershipID, props.siteKey, userPermissions, customerIDForTasks]);

  // Fetch payments (placeholder for now - would need an actual API)
  useEffect(() => {
    if (!membershipID || !membership) return;

    // Mock payment data for now - replace with actual API call when available
    if (membership.lastPaymentAmount && membership.lastPaymentDate) {
      setPayments([
        {
          id: "last-payment",
          amount: membership.lastPaymentAmount,
          date: membership.lastPaymentDate,
          method: "Card",
          status: "completed",
        },
      ]);
    }
  }, [membershipID, membership]);

  // Get status styling
  const getStatusClass = (status: string) => {
    switch (status) {
      case "active":
        return "bg-greenPass text-white";
      case "awaitingPayment":
        return "bg-orangeError text-white";
      case "canceled":
        return "bg-red-400 text-white";
      default:
        return "bg-primary text-primaryButtonText";
    }
  };

  // Handle field change
  const handleChange = (field: string, value: any) => {
    setEditedMembership({
      ...editedMembership,
      [field]: value,
    });
  };

  // Save changes
  const handleSave = async () => {
    if (!membershipID || !membership || !firebaseUser) return;

    setIsSaving(true);
    try {
      // Create the update data
      const updateData = {
        ...editedMembership,
        timestampLastModified: Timestamp.now(),
        lastModifiedBy: firebaseUser.uid,
      };

      // Prepare data for Firestore (removing id, refPath, and undefined values)
      const preparedData = MembershipManager.convertForFirestore(updateData);

      // Call update with correct parameter order (siteKey, id, data)
      await DbWrite.memberships.update(
        props.siteKey,
        membershipID,
        preparedData,
      );

      addMessage({
        id: createToastMessageID(),
        message: "Membership updated successfully",
        type: "success",
        dialog: false,
      });

      setIsEditing(false);
      setEditedMembership({});
    } catch (error) {
      logger.error("Error saving membership:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to update membership",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setEditedMembership({});
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return currencyFormatter(amount);
  };

  // Get next invoice date text
  const getNextInvoiceText = () => {
    if (!membership?.nextInvoiceDate) return null;

    return (
      <p className="text-sm">
        Next invoice: {convertToReadableTimestamp(membership.nextInvoiceDate)}
      </p>
    );
  };

  // Get task status class
  const getTaskStatusClass = (taskStatus: number) => {
    if (taskStatus === TaskStatus.COMPLETE) {
      return "bg-greenPass text-white";
    } else if (taskStatus === TaskStatus.IN_PROGRESS) {
      return "bg-blue-500 text-white";
    } else {
      return "bg-gray-200 text-gray-700";
    }
  };

  // Get payment status class
  const getPaymentStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-greenPass text-white";
      case "pending":
        return "bg-yellow-500 text-white";
      case "refunded":
        return "bg-red-400 text-white";
      default:
        return "bg-gray-200 text-gray-700";
    }
  };

  // Tab navigation
  const tabButtonClass = (tab: "details" | "payments") => {
    return `px-4 py-2 font-medium rounded-t-md ${
      activeTab === tab
        ? "bg-white border border-b-0 border-gray-300 text-primary"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`;
  };

  // Handle view invoice
  const handleViewInvoice = (invoiceID: string) => {
    // Navigate to invoice view or open invoice dialog
    addMessage({
      id: createToastMessageID(),
      message: `Viewing invoice ${invoiceID}`,
      type: "info",
      dialog: false,
    });
  };

  // Handle go to task
  const handleGoToTask = (taskId: string) => {
    if (!membership) return;

    // Find task to get its work record ID
    const task = tasks.find((t) => t.id === taskId);
    if (task && task.craftRecordID) {
      const parts = task.craftRecordID.split("/");
      const workRecordID = parts[parts.length - 1];
      navigate(`${WORK_RECORD_AND_TASKS_URL}/${workRecordID}/${taskId}`);
    }
  };

  // View payment
  const handleViewPayment = (paymentId: string) => {
    addMessage({
      id: createToastMessageID(),
      message: `Viewing payment ${paymentId}`,
      type: "info",
      dialog: false,
    });
  };

  // Add a function to handle scheduling a new task
  const handleScheduleNewTask = () => {
    if (!membership || !customerIDForTasks) return;

    // Here you would navigate to the task creation page or open a dialog
    // For now, just show a message
    addMessage({
      id: createToastMessageID(),
      message: "Scheduling a new task for this membership",
      type: "info",
      dialog: false,
    });

    // In a real implementation, you would navigate to the task creation page
    // with the membership pre-selected
    navigate(
      `/schedule-task?customerID=${customerIDForTasks}&membershipID=${membershipID}`,
    );
  };

  // Function to handle viewing all invoices
  const handleViewAllInvoices = () => {
    // This would navigate to a page showing all invoices for this membership
    // For now, just show a message
    addMessage({
      id: createToastMessageID(),
      message: "Viewing all invoices for this membership",
      type: "info",
      dialog: false,
    });

    // In a real implementation, you might navigate to an invoices page filtered by this membership
    navigate(`/invoices?membershipID=${membershipID}`);
  };

  // First, add a helper function to format dates without timestamps
  const formatDateOnly = (timestamp: Timestamp | null | undefined): string => {
    if (!timestamp) return "Not set";

    const date = timestamp.toDate();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Helper function to determine if a task is before, during, or after the membership period
  const getTaskTimeframe = (
    task: ExistingTask,
  ): "before" | "during" | "after" | "unknown" => {
    if (!membership?.membershipStartDate || !task.timestampScheduled)
      return "unknown";

    const taskDate = task.timestampScheduled.toDate();
    const startDate = membership.membershipStartDate.toDate();
    const endDate = membership.membershipEndDate
      ? membership.membershipEndDate.toDate()
      : null;

    if (taskDate < startDate) {
      return "before";
    } else if (endDate && taskDate > endDate) {
      return "after";
    } else {
      return "during";
    }
  };

  // Function to sort tasks chronologically
  const sortTasksByDate = (tasks: ExistingTask[]): ExistingTask[] => {
    return [...tasks].sort((a, b) => {
      if (!a.timestampScheduled && !b.timestampScheduled) return 0;
      if (!a.timestampScheduled) return 1;
      if (!b.timestampScheduled) return -1;

      // Sort in reverse chronological order (most recent first)
      return (
        b.timestampScheduled.toDate().getTime() -
        a.timestampScheduled.toDate().getTime()
      );
    });
  };

  // Function to fetch membership overview from Stilton
  const handleGetStiltonOverview = async () => {
    if (!membershipID) return;

    setIsLoadingStiltonOverview(true);
    try {
      const overview = await DbRead.stilton.getMembershipOverview({
        siteKeyID: props.siteKey,
        membershipID: membershipID,
      });
      setStiltonOverview(overview);
    } catch (error) {
      logger.error("Error getting Stilton membership overview:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to generate AI summary",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsLoadingStiltonOverview(false);
    }
  };

  // Handle task association with membership
  async function toggleTaskMembershipAssociation(
    task: ExistingTask,
  ): Promise<void> {
    if (!membershipID || !membership || !firebaseUser || !task.id) return;

    setIsLoadingTasks(true);
    try {
      // Check if the task is already associated with this membership
      const isTaskInMembership =
        task.membershipIDs && task.membershipIDs.includes(membershipID);

      // Create a new array of membership IDs
      let newMembershipIDs: string[] = [];

      if (isTaskInMembership) {
        // If task is already in membership, remove it
        newMembershipIDs =
          task.membershipIDs?.filter((id) => id !== membershipID) || [];
      } else {
        // If task is not in membership, add it
        newMembershipIDs = [...(task.membershipIDs || []), membershipID];
      }

      // Create the update data object
      const updateData = {
        membershipIDs: newMembershipIDs,
        isMembershipTask: true,
        timestampLastModified: Timestamp.now(),
        lastModifiedBy: firebaseUser.uid,
      };

      // Update the task in the database
      await DbWrite.tasks.update(updateData, props.siteKey, task.id);

      // Show success message
      addMessage({
        id: createToastMessageID(),
        message: isTaskInMembership
          ? "Task removed from membership"
          : "Task added to membership",
        type: "success",
        dialog: false,
      });

      // Refresh the tasks
      if (customerIDForTasks) {
        const customerTasks = await DbRead.tasks.getSingleCustomerTasks({
          permissions: userPermissions as SiteKeyUserPermissions,
          customerID: customerIDForTasks,
          siteKey: props.siteKey,
        });

        setAllTasks(customerTasks);

        // Filter tasks for this membership
        const membershipRelatedTasks = customerTasks.filter(
          (t) => t.membershipIDs && t.membershipIDs.includes(membershipID),
        );

        // Filter tasks not in this membership but for this customer
        const otherCustomerTasks = customerTasks.filter(
          (t) => !t.membershipIDs || !t.membershipIDs.includes(membershipID),
        );

        setMembershipTasks(membershipRelatedTasks);
        setOtherTasks(otherCustomerTasks);
      }
    } catch (error) {
      logger.error("Error updating task membership association:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to update task",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsLoadingTasks(false);
    }
  }

  // Function to link an invoice to this membership (placeholder)
  const handleLinkInvoiceToMembership = async (invoiceId: string) => {
    if (!membershipID) {
      return;
    }

    logger.info(`Linking invoice ${invoiceId} to membership ${membershipID}`);

    const invoice = customerInvoices.find(
      (invoice) => invoice.id === invoiceId,
    );
    if (!invoice) {
      logger.error(`Invoice ${invoiceId} not found in unlinkedInvoices`);
      addMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }

    const pbiIdToFindInLineItems =
      membershipTemplate?.priceBookItemID ??
      membershipTemplate?.renewalPriceBookItemID;

    if (!pbiIdToFindInLineItems) {
      logger.error(`No pbiID given for invoice ${invoiceId}`);
      addMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }

    const foundIndex = invoice.lineItems.findIndex(
      (lineItem) => lineItem.priceBookItemID === pbiIdToFindInLineItems,
    );
    if (foundIndex === -1) {
      logger.error(
        `lineItem with priceBookItemID ${pbiIdToFindInLineItems} not found for invoice ${invoiceId}`,
      );
      addMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }

    // add the membershipID to the lineItem
    invoice.lineItems[foundIndex].membershipID = membershipID;

    try {
      await DbWrite.invoices.linkToMembership({
        invoiceRefPath: invoice.refPath,
        lineItems: invoice.lineItems,
        membershipID: membershipID,
      });

      addMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Invoice linked to membership successfully",
        type: "success",
      });
    } catch (e) {
      logger.error(`linkInvoiceToMembership: ${JSON.stringify(e, null, 2)}`);
      addMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
    }
  };

  // Helper function to check if a task is already associated with another membership
  const isTaskLinkedToAnotherMembership = (task: ExistingTask): boolean => {
    if (!membershipID) return true;
    if (
      task.isMembershipTask === true &&
      (!task.membershipIDs || task.membershipIDs.length === 0)
    )
      return true;
    if (
      task.membershipIDs &&
      task.membershipIDs.length > 0 &&
      !task.membershipIDs.includes(membershipID)
    )
      return true;
    return false;
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  if (error || !membership || !customer || !membershipTemplate) {
    return (
      <div className="mt-4 rounded bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Error loading membership</p>
        <p>{error || "Required data not available"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 text-lg">
      {/* Header section with status, customer info, and actions */}
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row">
        <NavButtonBack />
        <div className="w-full">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">
              {membershipTemplate.title}
            </h1>
            <div
              className={`w-fit rounded-full px-2.5 py-1 text-sm font-medium ${getStatusClass(membership.status)} capitalize`}
            >
              {getReadableMembershipStatus(membership.status)}
            </div>
          </div>

          {/* Customer name and location moved to header */}
          {customer && (
            <div className="mt-1 text-sm text-gray-600">
              <a
                href={`/customers/${customer.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                {customer.firstName} {customer.lastName}
              </a>
              {customerLocation && (
                <span className="ml-2">
                  {customerLocation.locationName || "Primary Location"}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="ml-auto w-full text-right">
          {!isEditing ? (
            <BaseButtonPrimary
              onClick={() => setIsEditing(true)}
              className="ml-auto"
            >
              Edit Membership
            </BaseButtonPrimary>
          ) : (
            <div className="flex justify-end gap-2">
              <BaseButtonSecondary onClick={handleCancel}>
                Cancel
              </BaseButtonSecondary>
              <BaseButtonPrimary onClick={handleSave} isBusy={isSaving}>
                Save Changes
              </BaseButtonPrimary>
            </div>
          )}
          {getNextInvoiceText()}
        </div>
      </div>

      <div className="border-b border-gray-300"></div>

      {/* Stilton AI Membership Summary Section */}
      <div className="mb-2 rounded-lg border border-gray-300 p-4 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary">
            Membership Summary
          </h2>
          <button
            onClick={handleGetStiltonOverview}
            disabled={isLoadingStiltonOverview}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-default disabled:opacity-50"
          >
            <img
              src="/white_hardhat_transparent_bg.svg"
              alt="Stilton AI Icon"
              className="h-4 w-4"
            />
            {isLoadingStiltonOverview ? (
              <div className="relative flex h-4 w-4 items-center justify-center">
                <div className="absolute h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              </div>
            ) : stiltonOverview ? (
              "Refresh AI Summary"
            ) : (
              "Generate AI Summary"
            )}
          </button>
        </div>

        {isLoadingStiltonOverview && (
          <div className="mt-4 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
              <p className="mt-2 text-sm text-gray-600">
                Generating AI summary...
              </p>
            </div>
          </div>
        )}

        {!isLoadingStiltonOverview &&
          stiltonOverview &&
          showStiltonOverview && (
            <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
              <div className="flex justify-between">
                <h3 className="mb-2 flex items-center font-semibold text-purple-900">
                  <img
                    src="/purple_hardhat_transparent_bg.svg"
                    alt="Stilton AI Icon"
                    className="mr-2 h-5 w-5"
                  />
                  Stilton AI Membership Overview
                </h3>
                <button
                  onClick={() => setShowStiltonOverview(false)}
                  className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  title="Dismiss"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              <div className="prose prose-sm max-w-none text-gray-700">
                {stiltonOverview.split("\n").map((paragraph, idx) => (
                  <p key={idx} className="mb-2">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          )}

        {!isLoadingStiltonOverview && !stiltonOverview && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-600">
            <p>
              Generate an AI summary to get insights about this membership's
              status, payments, and tasks.
            </p>
          </div>
        )}

        {!isLoadingStiltonOverview &&
          stiltonOverview &&
          !showStiltonOverview && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowStiltonOverview(true)}
                className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
              >
                Show AI Summary
              </button>
            </div>
          )}
      </div>

      {/* Add the horizontal timeline component here, just after the Stilton section */}
      {membership && membershipTemplate && (
        <div className="rounded-lg border border-gray-300 p-4 shadow">
          <MembershipTimelineHorizontal
            membership={membership}
            membershipTemplate={membershipTemplate}
            invoices={membershipInvoices}
            payments={payments}
          />
        </div>
      )}

      {/* Awaiting Payment Alert */}
      {membership.status === "awaitingPayment" &&
        membershipInvoices.length > 0 && (
          <AlertV2
            variant="warning"
            title={
              membershipInvoices[0].invoiceNumber
                ? `This membership will remain in Awaiting Payment status until invoice ${membershipInvoices[0].invoiceNumber} is paid.`
                : "This membership will remain in Awaiting Payment status until the invoice is paid."
            }
            actions={
              <BaseButtonSecondary
                onClick={() => handleViewInvoice(membershipInvoices[0].id)}
              >
                {`View Invoice${membershipInvoices[0].invoiceNumber ? ` ${membershipInvoices[0].invoiceNumber}` : ""}`}
              </BaseButtonSecondary>
            }
          />
        )}

      {/* No Invoices Found Alert */}
      {noInvoicesFound && (
        <AlertV2
          variant="error"
          title="No invoices found for this membership"
          message="There are no invoices associated with this membership. Please link an invoice or create a new one."
          actions={
            <BaseButtonSecondary
              onClick={() =>
                navigate(
                  `/invoices/create?customerID=${customer?.id}&membershipID=${membershipID}`,
                )
              }
            >
              Create New Invoice
            </BaseButtonSecondary>
          }
        />
      )}

      {/* Display customer invoices when no membership invoices are found - Moved to be directly after the alert */}
      {noInvoicesFound && customerInvoices.length > 0 && (
        <div className="mt-4 rounded border border-gray-300 p-4 shadow">
          <h2 className="mb-4 text-lg font-semibold text-primary">
            Available Customer Invoices
          </h2>

          {isLoadingCustomerInvoices ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Invoice #
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Date
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Amount
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Location
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Membership Item
                    </th>
                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {customerInvoices
                    .sort(
                      (a, b) => a.issueDate.toMillis() - b.issueDate.toMillis(),
                    )
                    .map((invoice) => {
                      // Check if the invoice's location matches the membership's location
                      const locationMatches =
                        invoice.customerLocationID ===
                        membership.customerLocationID;

                      // Check if the invoice contains a line item with the membership's priceBookItemID
                      const hasMembershipItem = invoice.lineItems?.some(
                        (item) =>
                          (membershipTemplate?.priceBookItemID &&
                            item.priceBookItemID ===
                              membershipTemplate.priceBookItemID) ||
                          (membershipTemplate?.renewalPriceBookItemID &&
                            item.priceBookItemID ===
                              membershipTemplate.renewalPriceBookItemID),
                      );

                      // Check if invoice is already linked to another membership
                      const membershipIDs = invoice.customData
                        ?.membershipIDs as string[] | undefined;
                      const isAlreadyLinked =
                        membershipIDs &&
                        Array.isArray(membershipIDs) &&
                        membershipIDs.length > 0;

                      return (
                        <tr
                          key={invoice.id}
                          className={`hover:bg-gray-50 ${locationMatches ? "bg-green-50" : ""} ${hasMembershipItem ? "font-medium" : ""} ${isAlreadyLinked ? "bg-gray-100" : ""}`}
                        >
                          <td className="whitespace-nowrap py-2 text-sm font-medium">
                            {invoice.invoiceNumber || "N/A"}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm">
                            {invoice.issueDate
                              ? formatDateOnly(invoice.issueDate)
                              : "N/A"}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm font-medium">
                            {formatCurrency(invoice.totalAmount || 0)}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                invoice.status === StiltInvoiceStatus.PAID
                                  ? "bg-greenPass text-white"
                                  : "bg-orangeError text-white"
                              }`}
                            >
                              {invoice.status === StiltInvoiceStatus.PAID
                                ? "Paid"
                                : "Unpaid"}
                            </span>
                          </td>
                          <td className="py-2 text-sm">
                            <div className="flex items-center">
                              {locationMatches ? (
                                <span className="flex items-center text-green-700">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="mr-1 h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  Matches membership
                                </span>
                              ) : (
                                <span className="flex items-center text-yellow-700">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="mr-1 h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                  </svg>
                                  Different location
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 text-sm">
                            {hasMembershipItem ? (
                              <span className="flex items-center text-primary">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="mr-1 h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                Includes Membership Item
                              </span>
                            ) : (
                              <span className="text-gray-400">
                                No membership item
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap py-2 text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleViewInvoice(invoice.id)}
                                className="rounded-full bg-gray-100 p-1 text-gray-600 hover:bg-gray-200"
                                title="View Invoice Details"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="h-4 w-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              </button>
                              {isAlreadyLinked ? (
                                <div className="relative inline-flex">
                                  <button
                                    disabled
                                    className="cursor-not-allowed rounded-full bg-gray-300 p-1 text-gray-500"
                                    title="Already linked to another membership"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.5}
                                      stroke="currentColor"
                                      className="h-4 w-4"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                      />
                                    </svg>
                                  </button>
                                  <div className="absolute bottom-full mb-2 w-48 -translate-x-1/2 transform rounded-md bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                    Already linked to another membership
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleLinkInvoiceToMembership(invoice.id)
                                  }
                                  className="hover:bg-primary-dark rounded-full bg-primary p-1 text-white"
                                  title="Link to Membership"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-4 w-4"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {isAlreadyLinked && (
                              <div className="mt-1 text-xs text-gray-500">
                                Already linked to other membership
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Display message when no customer invoices are found */}
      {noInvoicesFound &&
        customerInvoices.length === 0 &&
        !isLoadingCustomerInvoices && (
          <div className="mt-4 rounded border border-gray-300 bg-gray-50 p-4 text-center shadow">
            <h3 className="text-lg font-medium text-gray-700">
              No Invoices Available
            </h3>
            <p className="mt-2 text-gray-600">
              This customer doesn't have any invoices that can be linked to this
              membership.
            </p>
            <button
              onClick={() =>
                navigate(
                  `/invoices/create?customerID=${customer?.id}&membershipID=${membershipID}`,
                )
              }
              className="hover:bg-primary-dark mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Create New Invoice
            </button>
          </div>
        )}

      <div className="space-y-6">
        {/* 1. MEMBERSHIP OVERVIEW SECTION */}
        <div className="rounded border border-gray-300 p-4 shadow">
          <h2 className="mb-4 text-lg font-semibold text-primary">
            Membership Overview
          </h2>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Customer and Membership Info */}
            <div className="space-y-4">
              {/* Customer Name */}
              <div>
                <p className="text-sm font-semibold text-gray-600">Customer</p>
                <p className="text-base font-medium">
                  {customer && (
                    <Link
                      to={`/customers/${customer.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {customer.firstName} {customer.lastName}
                    </Link>
                  )}
                </p>
              </div>

              {/* Location/Address */}
              <div>
                <p className="text-sm font-semibold text-gray-600">Location</p>
                {customerLocation ? (
                  <div className="text-base">
                    <p className="font-medium">
                      {customerLocation.locationName || "Primary Address"}
                    </p>
                    <p>{customerLocation.addressLine1 || ""}</p>
                    {customerLocation.addressLine2 && (
                      <p>{customerLocation.addressLine2}</p>
                    )}
                    <p>
                      {customerLocation.city || ""}
                      {customerLocation.city ? ", " : ""}
                      {customerLocation.state || ""}{" "}
                      {customerLocation.zipCode || ""}
                    </p>
                  </div>
                ) : (
                  <p className="text-base font-medium text-red-600">
                    No Location Selected
                  </p>
                )}
              </div>

              {/* Equipment Information */}
              <div>
                <p className="text-sm font-semibold text-gray-600">Equipment</p>
                {membership.assetIDs && membership.assetIDs.length > 0 ? (
                  <p className="text-base font-medium">
                    <Link
                      to={`/assets/${membership.assetIDs[0]}`}
                      className="text-blue-600 hover:underline"
                    >
                      View Equipment
                    </Link>
                    {membership.assetIDs.length > 1 && (
                      <span className="ml-1 text-sm text-gray-500">
                        (+{membership.assetIDs.length - 1} more)
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-base text-gray-500">None Selected</p>
                )}
              </div>

              {/* Membership Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600">
                    Start Date
                  </p>
                  <p className="text-base">
                    {formatDateOnly(membership?.membershipStartDate)}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-600">
                    End Date
                  </p>
                  <p className="text-base">
                    {formatDateOnly(membership?.membershipEndDate)}
                  </p>
                </div>
              </div>

              {/* Membership Status */}
              <div>
                <p className="text-sm font-semibold text-gray-600">Status</p>
                <p className="text-base">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-sm font-medium ${getStatusClass(membership.status)}`}
                  >
                    {getReadableMembershipStatus(membership.status)}
                  </span>
                </p>
              </div>

              {/* Membership Notes */}
              {membership?.notes && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Notes</p>
                  <p className="whitespace-pre-line text-base">
                    {membership.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Template Information - Highlighted */}
            {membershipTemplate && (
              <div className="rounded-md bg-green-50 p-4">
                <h3 className="mb-3 text-base font-semibold text-gray-800">
                  Membership Template
                </h3>

                <div className="mb-3">
                  <p className="text-sm font-semibold text-gray-600">
                    Template
                  </p>
                  <p className="text-base">
                    <StiltLink
                      to={`${MEMBERSHIP_TEMPLATES}/${membershipTemplate.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {membershipTemplate.title}
                    </StiltLink>
                  </p>
                </div>

                <div className="mb-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">
                      Duration
                    </p>
                    <p className="text-base font-medium">
                      {membershipTemplate.defaultDuration === -1
                        ? "Indefinite"
                        : membershipTemplate.defaultDuration === 12
                          ? "1 year"
                          : `${membershipTemplate.defaultDuration} months`}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-600">Price</p>
                    <p className="text-base font-medium">
                      {formatCurrency(membershipTemplate.price)}
                    </p>
                  </div>
                </div>

                {/* Task Count Information */}
                {membershipTemplate.taskGeneration &&
                  membershipTemplate.taskGeneration.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-600">
                        Tasks
                      </p>
                      <p className="text-base font-medium">
                        {membershipTemplate.taskGeneration.length}{" "}
                        {membershipTemplate.taskGeneration.length === 1
                          ? "task"
                          : "tasks"}{" "}
                        scheduled
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>

        {/* Make Invoices & Payments and Tasks sections side-by-side */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
          {/* 2. INVOICES & PAYMENTS SECTION - Now vertically stacked */}
          <div className="rounded border border-gray-300 p-4 shadow">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-primary">
                Invoices & Payments
              </h2>

              {/* View All Invoices button */}
              <button
                onClick={handleViewAllInvoices}
                className="hover:bg-primary-700 inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-white"
              >
                <DocumentIcon className="mr-1 h-5 w-5" />
                View All Invoices
              </button>
            </div>

            <div className="space-y-6">
              {/* Financial Information */}
              <div className="space-y-4">
                {/* Next Invoice */}
                {membershipInvoices.length > 0 && (
                  <div className="rounded-md bg-green-50 p-3">
                    <p className="text-sm font-semibold text-gray-600">
                      Next Invoice
                    </p>
                    <p className="text-base font-medium">
                      {membershipInvoices[0].issueDate
                        ? formatDateOnly(membershipInvoices[0].issueDate)
                        : formatDateOnly(membership.nextInvoiceDate)}{" "}
                      -
                      {membershipTemplate &&
                        ` ${formatCurrency(membershipTemplate.price)}`}
                    </p>
                  </div>
                )}

                {/* Last Payment */}
                {membership.lastPaymentDate && membership.lastPaymentAmount && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600">
                      Last Payment
                    </p>
                    <p className="text-base">
                      {formatCurrency(membership.lastPaymentAmount)} on{" "}
                      {formatDateOnly(membership.lastPaymentDate)}
                    </p>
                  </div>
                )}

                {/* Invoice Frequency & Method */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">
                      Invoice Frequency
                    </p>
                    <p className="text-base font-medium capitalize">
                      {membershipTemplate?.invoiceFrequency || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-600">
                      Invoice Method
                    </p>
                    <p className="text-base font-medium">
                      {membershipTemplate &&
                        getReadableInvoiceMethod(
                          membershipTemplate.invoiceMethod,
                        )}
                    </p>
                  </div>
                </div>

                {/* Invoice Settings */}
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="mb-2 text-sm font-semibold text-gray-600">
                    Invoice Settings
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex items-center">
                      <div
                        className={`mr-2 h-3 w-3 rounded-full ${(membership?.automaticallyPayInvoice ?? false) ? "bg-green-500" : "bg-gray-300"}`}
                      ></div>
                      <p className="text-sm">
                        {(membership?.automaticallyPayInvoice ?? false)
                          ? "Automatically process payments"
                          : "No automatic payment processing"}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div
                        className={`mr-2 h-3 w-3 rounded-full ${(membership?.automaticallySendInvoice ?? false) ? "bg-green-500" : "bg-gray-300"}`}
                      ></div>
                      <p className="text-sm">
                        {(membership?.automaticallySendInvoice ?? false)
                          ? "Automatically email invoices"
                          : "No automatic invoice emails"}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div
                        className={`mr-2 h-3 w-3 rounded-full ${(membership?.automaticallySendReceipt ?? false) ? "bg-green-500" : "bg-gray-300"}`}
                      ></div>
                      <p className="text-sm">
                        {(membership?.automaticallySendReceipt ?? false)
                          ? "Automatically email receipts"
                          : "No automatic receipt emails"}
                      </p>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoPayInvoice"
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          checked={
                            editedMembership.automaticallyPayInvoice !==
                            undefined
                              ? editedMembership.automaticallyPayInvoice
                              : membership.automaticallyPayInvoice || false
                          }
                          onChange={(e) =>
                            handleChange(
                              "automaticallyPayInvoice",
                              e.target.checked,
                            )
                          }
                        />
                        <label
                          htmlFor="autoPayInvoice"
                          className="ml-2 text-sm"
                        >
                          Auto-Pay Invoices
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoSendInvoice"
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          checked={
                            editedMembership.automaticallySendInvoice !==
                            undefined
                              ? editedMembership.automaticallySendInvoice
                              : membership.automaticallySendInvoice || false
                          }
                          onChange={(e) =>
                            handleChange(
                              "automaticallySendInvoice",
                              e.target.checked,
                            )
                          }
                        />
                        <label
                          htmlFor="autoSendInvoice"
                          className="ml-2 text-sm"
                        >
                          Auto-Send Invoices
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoSendReceipt"
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          checked={
                            editedMembership.automaticallySendReceipt !==
                            undefined
                              ? editedMembership.automaticallySendReceipt
                              : membership.automaticallySendReceipt || false
                          }
                          onChange={(e) =>
                            handleChange(
                              "automaticallySendReceipt",
                              e.target.checked,
                            )
                          }
                        />
                        <label
                          htmlFor="autoSendReceipt"
                          className="ml-2 text-sm"
                        >
                          Auto-Send Receipts
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment History - Now below Financial Information */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-600">
                  Payment History
                </h3>

                {payments && payments.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Date
                        </th>
                        <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Amount
                        </th>
                        <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Method
                        </th>
                        <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-2 text-sm">
                            {formatDateOnly(payment.date)}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm font-medium">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm">
                            {payment.method}
                          </td>
                          <td className="whitespace-nowrap py-2 text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleViewPayment(payment.id)}
                                className="rounded-full bg-gray-100 p-1 text-gray-600 hover:bg-gray-200"
                                title="View Payment Details"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="h-4 w-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              </button>
                              {membershipInvoices.length > 0 && (
                                <button
                                  onClick={() =>
                                    handleLinkInvoiceToMembership(
                                      membershipInvoices[0].id,
                                    )
                                  }
                                  className="rounded-full bg-gray-100 p-1 text-gray-600 hover:bg-gray-200"
                                  title="Link Invoice"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-4 w-4"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500">No payment history available.</p>
                )}
              </div>
            </div>
          </div>

          {/* 3. TASKS SECTION - Now vertically stacked */}
          <div className="rounded border border-gray-300 p-4 shadow">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-primary">Tasks</h2>

              <button
                onClick={handleScheduleNewTask}
                className="hover:bg-primary-700 inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-white"
              >
                <PlusCircleIcon className="mr-1 h-5 w-5" />
                Schedule Task
              </button>
            </div>

            {/* Task settings info */}
            <div className="mb-4 rounded border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center">
                <div
                  className={`mr-2 h-3 w-3 rounded-full ${(membership?.automaticallyGenerateTasks ?? false) ? "bg-green-500" : "bg-gray-300"}`}
                ></div>
                <p className="text-sm">
                  {membershipTemplate &&
                  membershipTemplate.taskGeneration &&
                  membershipTemplate.taskGeneration.length > 0
                    ? (membership?.automaticallyGenerateTasks ?? false)
                      ? `${membershipTemplate.taskGeneration.length} tasks are automatically generated`
                      : `${membershipTemplate.taskGeneration.length} tasks need to be manually created`
                    : "No task schedule defined for this membership type"}
                </p>
              </div>

              {isEditing && (
                <div className="mt-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="autoGenTasks"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={
                        editedMembership.automaticallyGenerateTasks !==
                        undefined
                          ? editedMembership.automaticallyGenerateTasks
                          : membership.automaticallyGenerateTasks || false
                      }
                      onChange={(e) =>
                        handleChange(
                          "automaticallyGenerateTasks",
                          e.target.checked,
                        )
                      }
                    />
                    <label htmlFor="autoGenTasks" className="ml-2 text-sm">
                      Automatically Generate Tasks
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Task dates overview */}
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-600">
                  Next Task Date
                </p>
                <p className="text-base">
                  {membership.nextScheduledTaskDate
                    ? formatDateOnly(membership.nextScheduledTaskDate)
                    : "None scheduled"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-600">
                  Last Task Date
                </p>
                <p className="text-base">
                  {membership.lastCompletedTaskDate
                    ? formatDateOnly(membership.lastCompletedTaskDate)
                    : "No tasks completed"}
                </p>
              </div>
            </div>

            {/* Task Management - Two Column Layout */}
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Membership Tasks Column */}
              <div>
                <h3 className="mb-2 text-base font-medium text-primary">
                  Membership Tasks
                </h3>
                <p className="mb-3 text-sm text-gray-500">
                  Tasks that are directly associated with this membership
                </p>

                {isLoadingTasks ? (
                  <div className="flex h-32 items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
                  </div>
                ) : membershipTasks.length > 0 ? (
                  <div className="space-y-2">
                    {sortTasksByDate(membershipTasks).map((task) => (
                      <div
                        key={task.id}
                        className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50"
                      >
                        <div
                          className="flex grow cursor-pointer items-start gap-3"
                          onClick={() => task.id && handleGoToTask(task.id)}
                        >
                          <div className="flex-1">
                            <h4 className="font-medium">
                              {task.title || "Untitled Task"}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusClass(task.taskStatus)}`}
                              >
                                {getReadableTaskStatus(task.taskStatus)}
                              </span>
                              <span>
                                {task.timestampScheduled
                                  ? `Scheduled: ${formatDateOnly(task.timestampScheduled)}`
                                  : "No scheduled date"}
                              </span>
                              {task.timestampTaskCompleted && (
                                <span>
                                  • Completed:{" "}
                                  {formatDateOnly(task.timestampTaskCompleted)}
                                </span>
                              )}
                            </div>
                            {/* Task notes/description */}
                            {task.description && (
                              <div className="mt-2 text-xs text-gray-600">
                                <p className="italic">{task.description}</p>
                              </div>
                            )}
                            {/* Task completion notes */}
                            {task.taskSpecificDetails?.completionNotes && (
                              <div className="mt-2 text-xs text-gray-600">
                                <p className="font-medium">Completion Notes:</p>
                                <p className="italic">
                                  {String(
                                    task.taskSpecificDetails.completionNotes,
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-700 hover:bg-red-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTaskMembershipAssociation(task);
                          }}
                          title="Remove from membership"
                        >
                          <MinusIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
                    <p className="text-gray-500">
                      No membership tasks have been created yet.
                    </p>
                    <button
                      onClick={handleScheduleNewTask}
                      className="mt-3 inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      <PlusCircleIcon className="mr-1 h-5 w-5" />
                      Schedule Task
                    </button>
                  </div>
                )}
              </div>

              {/* Other Customer Tasks Column */}
              <div>
                <h3 className="mb-2 text-base font-medium text-primary">
                  Other Customer Tasks
                </h3>
                <p className="mb-3 text-sm text-gray-500">
                  Tasks for this customer that are not associated with this
                  membership
                </p>

                {isLoadingTasks ? (
                  <div className="flex h-32 items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
                  </div>
                ) : otherTasks.length > 0 ? (
                  <div className="h-[400px] overflow-y-auto pr-1">
                    <div className="space-y-2 pr-2">
                      {(() => {
                        // Sort tasks by date
                        const sortedTasks = sortTasksByDate(otherTasks);

                        // Group tasks by timeframe
                        const tasksBefore: ExistingTask[] = [];
                        const tasksDuring: ExistingTask[] = [];
                        const tasksAfter: ExistingTask[] = [];
                        const tasksUnknown: ExistingTask[] = [];

                        sortedTasks.forEach((task) => {
                          const timeframe = getTaskTimeframe(task);
                          if (timeframe === "before") tasksBefore.push(task);
                          else if (timeframe === "during")
                            tasksDuring.push(task);
                          else if (timeframe === "after") tasksAfter.push(task);
                          else tasksUnknown.push(task);
                        });

                        // Combine all tasks with dividers
                        const result: React.ReactNode[] = [];

                        // Add tasks before membership start
                        if (tasksBefore.length > 0) {
                          if (membership?.membershipStartDate) {
                            result.push(
                              <div key="before-label" className="my-3">
                                <div className="text-xs font-medium text-gray-500">
                                  Tasks before membership start (
                                  {formatDateOnly(
                                    membership.membershipStartDate,
                                  )}
                                  )
                                </div>
                              </div>,
                            );
                          }

                          // Wrap all before tasks in a container with red background
                          result.push(
                            <div
                              key="before-tasks-container"
                              className="mb-4 space-y-2 rounded-lg bg-red-50 p-3"
                            >
                              {tasksBefore.map((task) => (
                                <div
                                  key={task.id}
                                  className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50"
                                >
                                  <div
                                    className="flex grow cursor-pointer items-start gap-3"
                                    onClick={() =>
                                      task.id && handleGoToTask(task.id)
                                    }
                                  >
                                    <div className="flex-1">
                                      <h4 className="font-medium">
                                        {task.title || "Untitled Task"}
                                      </h4>
                                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                        <span
                                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusClass(task.taskStatus)}`}
                                        >
                                          {getReadableTaskStatus(
                                            task.taskStatus,
                                          )}
                                        </span>
                                        <span>
                                          {task.timestampScheduled
                                            ? `Scheduled: ${formatDateOnly(task.timestampScheduled)}`
                                            : "No scheduled date"}
                                        </span>
                                        {task.timestampTaskCompleted && (
                                          <span>
                                            • Completed:{" "}
                                            {formatDateOnly(
                                              task.timestampTaskCompleted,
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {/* Task notes/description */}
                                      {task.description && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="italic">
                                            {task.description}
                                          </p>
                                        </div>
                                      )}
                                      {/* Task completion notes */}
                                      {task.taskSpecificDetails
                                        ?.completionNotes && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="font-medium">
                                            Completion Notes:
                                          </p>
                                          <p className="italic">
                                            {String(
                                              task.taskSpecificDetails
                                                .completionNotes,
                                            )}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    className={`ml-2 flex h-8 w-8 items-center justify-center rounded-full ${
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        isTaskLinkedToAnotherMembership(task)
                                      ) {
                                        // Task is already linked to another membership, don't allow toggling
                                        return;
                                      }
                                      toggleTaskMembershipAssociation(task);
                                    }}
                                    title={
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "Task already linked to another membership"
                                        : "Add to membership"
                                    }
                                  >
                                    <PlusIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              ))}
                            </div>,
                          );
                        }

                        // Add separator and tasks during membership
                        if (tasksDuring.length > 0) {
                          // Calculate the date range for the membership period
                          const startDateStr = membership?.membershipStartDate
                            ? formatDateOnly(membership.membershipStartDate)
                            : "Unknown";
                          const endDateStr = membership?.membershipEndDate
                            ? formatDateOnly(membership.membershipEndDate)
                            : "Ongoing";

                          result.push(
                            <div
                              key="during-separator"
                              className="relative my-4"
                            >
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-green-500"></div>
                              </div>
                              <div className="relative flex justify-center">
                                <span className="bg-white px-3 text-xs font-medium text-green-600">
                                  Membership Period ({startDateStr} -{" "}
                                  {endDateStr})
                                </span>
                              </div>
                            </div>,
                          );

                          result.push(
                            <div
                              key="during-tasks-container"
                              className="mb-4 space-y-2 rounded-lg bg-green-50 p-3"
                            >
                              {tasksDuring.map((task) => (
                                <div
                                  key={task.id}
                                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50"
                                >
                                  <div
                                    className="flex grow cursor-pointer items-start gap-3"
                                    onClick={() =>
                                      task.id && handleGoToTask(task.id)
                                    }
                                  >
                                    <div className="flex-1">
                                      <h4 className="font-medium">
                                        {task.title || "Untitled Task"}
                                      </h4>
                                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                        <span
                                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusClass(task.taskStatus)}`}
                                        >
                                          {getReadableTaskStatus(
                                            task.taskStatus,
                                          )}
                                        </span>
                                        <span>
                                          {task.timestampScheduled
                                            ? `Scheduled: ${formatDateOnly(task.timestampScheduled)}`
                                            : "No scheduled date"}
                                        </span>
                                        {task.timestampTaskCompleted && (
                                          <span>
                                            • Completed:{" "}
                                            {formatDateOnly(
                                              task.timestampTaskCompleted,
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {/* Task notes/description */}
                                      {task.description && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="italic">
                                            {task.description}
                                          </p>
                                        </div>
                                      )}
                                      {/* Task completion notes */}
                                      {task.taskSpecificDetails
                                        ?.completionNotes && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="font-medium">
                                            Completion Notes:
                                          </p>
                                          <p className="italic">
                                            {String(
                                              task.taskSpecificDetails
                                                .completionNotes,
                                            )}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    className={`ml-2 flex h-8 w-8 items-center justify-center rounded-full ${
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        isTaskLinkedToAnotherMembership(task)
                                      ) {
                                        // Task is already linked to another membership, don't allow toggling
                                        return;
                                      }
                                      toggleTaskMembershipAssociation(task);
                                    }}
                                    title={
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "Task already linked to another membership"
                                        : "Add to membership"
                                    }
                                  >
                                    <PlusIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              ))}
                            </div>,
                          );
                        }

                        // Add separator and tasks after membership end
                        if (
                          tasksAfter.length > 0 &&
                          membership?.membershipEndDate
                        ) {
                          result.push(
                            <div
                              key="after-separator"
                              className="relative my-4"
                            >
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-red-400"></div>
                              </div>
                              <div className="relative flex justify-center">
                                <span className="bg-white px-3 text-xs font-medium text-red-600">
                                  After Membership End (
                                  {formatDateOnly(membership.membershipEndDate)}
                                  )
                                </span>
                              </div>
                            </div>,
                          );

                          // Wrap all after tasks in a container with red background
                          result.push(
                            <div
                              key="after-tasks-container"
                              className="mb-4 space-y-2 rounded-lg bg-red-50 p-3"
                            >
                              {tasksAfter.map((task) => (
                                <div
                                  key={task.id}
                                  className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50"
                                >
                                  <div
                                    className="flex grow cursor-pointer items-start gap-3"
                                    onClick={() =>
                                      task.id && handleGoToTask(task.id)
                                    }
                                  >
                                    <div className="flex-1">
                                      <h4 className="font-medium">
                                        {task.title || "Untitled Task"}
                                      </h4>
                                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                        <span
                                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusClass(task.taskStatus)}`}
                                        >
                                          {getReadableTaskStatus(
                                            task.taskStatus,
                                          )}
                                        </span>
                                        <span>
                                          {task.timestampScheduled
                                            ? `Scheduled: ${formatDateOnly(task.timestampScheduled)}`
                                            : "No scheduled date"}
                                        </span>
                                        {task.timestampTaskCompleted && (
                                          <span>
                                            • Completed:{" "}
                                            {formatDateOnly(
                                              task.timestampTaskCompleted,
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {/* Task notes/description */}
                                      {task.description && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="italic">
                                            {task.description}
                                          </p>
                                        </div>
                                      )}
                                      {/* Task completion notes */}
                                      {task.taskSpecificDetails
                                        ?.completionNotes && (
                                        <div className="mt-2 text-xs text-gray-600">
                                          <p className="font-medium">
                                            Completion Notes:
                                          </p>
                                          <p className="italic">
                                            {String(
                                              task.taskSpecificDetails
                                                .completionNotes,
                                            )}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    className={`ml-2 flex h-8 w-8 items-center justify-center rounded-full ${
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        isTaskLinkedToAnotherMembership(task)
                                      ) {
                                        // Task is already linked to another membership, don't allow toggling
                                        return;
                                      }
                                      toggleTaskMembershipAssociation(task);
                                    }}
                                    title={
                                      isTaskLinkedToAnotherMembership(task)
                                        ? "Task already linked to another membership"
                                        : "Add to membership"
                                    }
                                  >
                                    <PlusIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              ))}
                            </div>,
                          );
                        }

                        // Add tasks with unknown dates (if any)
                        if (tasksUnknown.length > 0) {
                          result.push(
                            <div
                              key="unknown-separator"
                              className="relative my-4"
                            >
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                              </div>
                              <div className="relative flex justify-center">
                                <span className="bg-white px-3 text-xs font-medium text-gray-500">
                                  Tasks with No Schedule Date
                                </span>
                              </div>
                            </div>,
                          );

                          tasksUnknown.forEach((task) => {
                            result.push(
                              <div
                                key={task.id}
                                className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50"
                              >
                                <div
                                  className="flex grow cursor-pointer items-start gap-3"
                                  onClick={() =>
                                    task.id && handleGoToTask(task.id)
                                  }
                                >
                                  <div className="flex-1">
                                    <h4 className="font-medium">
                                      {task.title || "Untitled Task"}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                      <span
                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusClass(task.taskStatus)}`}
                                      >
                                        {getReadableTaskStatus(task.taskStatus)}
                                      </span>
                                      <span>No scheduled date</span>
                                      {task.timestampTaskCompleted && (
                                        <span>
                                          • Completed:{" "}
                                          {formatDateOnly(
                                            task.timestampTaskCompleted,
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {/* Task notes/description */}
                                    {task.description && (
                                      <div className="mt-2 text-xs text-gray-600">
                                        <p className="italic">
                                          {task.description}
                                        </p>
                                      </div>
                                    )}
                                    {/* Task completion notes */}
                                    {task.taskSpecificDetails
                                      ?.completionNotes && (
                                      <div className="mt-2 text-xs text-gray-600">
                                        <p className="font-medium">
                                          Completion Notes:
                                        </p>
                                        <p className="italic">
                                          {String(
                                            task.taskSpecificDetails
                                              .completionNotes,
                                          )}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  className={`ml-2 flex h-8 w-8 items-center justify-center rounded-full ${
                                    isTaskLinkedToAnotherMembership(task)
                                      ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                      : "bg-green-100 text-green-700 hover:bg-green-200"
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isTaskLinkedToAnotherMembership(task)) {
                                      // Task is already linked to another membership, don't allow toggling
                                      return;
                                    }
                                    toggleTaskMembershipAssociation(task);
                                  }}
                                  title={
                                    isTaskLinkedToAnotherMembership(task)
                                      ? "Task already linked to another membership"
                                      : "Add to membership"
                                  }
                                >
                                  <PlusIcon className="h-5 w-5" />
                                </button>
                              </div>,
                            );
                          });
                        }

                        // Return all components
                        return result;
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
                    <p className="text-gray-500">
                      No other tasks found for this customer.
                    </p>
                    <button
                      onClick={handleScheduleNewTask}
                      className="mt-3 inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      <PlusCircleIcon className="mr-1 h-5 w-5" />
                      Schedule General Task
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
